import React, { useState, useEffect } from 'react'
import { TextField, TextFieldProps } from '@mui/material'
import {
  formatUSPhone,
  validateUSPhone,
  getUSPhoneErrorMessage,
} from '../../utils/phoneUtils'

export interface PhoneInputProps
  extends Omit<TextFieldProps, 'onChange' | 'value'> {
  value: string
  onChange: (value: string) => void
  onValidationChange?: (isValid: boolean) => void
  showErrorOnBlur?: boolean
  required?: boolean
}

/**
 * Reusable US Phone Input Component
 *
 * Features:
 * - Automatic formatting as (XXX) XXX-XXXX
 * - Real-time US phone number validation
 * - Customizable error messages
 * - Integrates with Material-UI TextField
 *
 * @example
 * ```tsx
 * <PhoneInput
 *   value={phoneNumber}
 *   onChange={(value) => setPhoneNumber(value)}
 *   label="Phone Number"
 *   required
 * />
 * ```
 */
const PhoneInput: React.FC<PhoneInputProps> = ({
  value,
  onChange,
  onValidationChange,
  showErrorOnBlur = true,
  required = false,
  error: externalError,
  helperText: externalHelperText,
  placeholder = '(555) 123-4567',
  ...textFieldProps
}) => {
  const [internalError, setInternalError] = useState('')
  const [isBlurred, setIsBlurred] = useState(false)

  // Validate on value change
  useEffect(() => {
    if (value && value.trim()) {
      const errorMessage = getUSPhoneErrorMessage(value)
      setInternalError(errorMessage)

      // Notify parent component of validation status
      if (onValidationChange) {
        onValidationChange(!errorMessage && validateUSPhone(value))
      }
    } else {
      setInternalError('')
      if (onValidationChange) {
        onValidationChange(!required)
      }
    }
  }, [value, onValidationChange, required])

  const handleChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const inputValue = event.target.value
    // Format the phone number as user types
    const formattedPhone = formatUSPhone(inputValue)
    onChange(formattedPhone)
  }

  const handleBlur = (event: React.FocusEvent<HTMLInputElement>) => {
    setIsBlurred(true)

    // Call original onBlur if provided
    if (textFieldProps.onBlur) {
      textFieldProps.onBlur(event)
    }

    // Show error on blur if enabled
    if (showErrorOnBlur) {
      const errorMessage = getUSPhoneErrorMessage(value)
      setInternalError(errorMessage)
    }
  }

  const handleFocus = (event: React.FocusEvent<HTMLInputElement>) => {
    // Call original onFocus if provided
    if (textFieldProps.onFocus) {
      textFieldProps.onFocus(event)
    }
  }

  // Determine which error to show (external takes precedence)
  const displayError =
    externalError !== undefined ? externalError : !!internalError
  const displayHelperText =
    externalHelperText !== undefined
      ? externalHelperText
      : isBlurred || internalError
        ? internalError
        : ''

  return (
    <TextField
      {...textFieldProps}
      value={value}
      onChange={handleChange}
      onBlur={handleBlur}
      onFocus={handleFocus}
      placeholder={placeholder}
      error={displayError}
      helperText={displayHelperText}
      inputProps={{
        maxLength: 14, // (XXX) XXX-XXXX = 14 characters
        ...textFieldProps.inputProps,
      }}
    />
  )
}

export default PhoneInput
